import React, { useState } from 'react';
import classNames from 'classnames';

import CurrencySelector from '../../../../components/CurrencySelector/CurrencySelector';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { ensureCurrentUser } from '../../../../util/data';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';

import {
  Avatar,
  InlineTextButton,
  Logo,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import {
  DARK_GLOOMY_NAVIGATION,
  DarkGloomyNavigationLink,
  isNavigationItemCurrent,
} from '../DarkGloomyNavigation';
import SearchIcon from '../SearchIcon';
import css from './TopbarMobileMenu.module.css';

const NavigationGroup = ({ currentPage, item }) => {
  const isCurrentGroup = item.children.some(child => isNavigationItemCurrent(child, currentPage));
  const [isOpen, setIsOpen] = useState(isCurrentGroup);

  return (
    <details
      className={css.navigationGroup}
      open={isOpen}
      onToggle={event => setIsOpen(event.currentTarget.open)}
    >
      <summary
        className={classNames(css.primaryNavigationLink, {
          [css.currentPrimaryNavigationLink]: isCurrentGroup,
        })}
      >
        <FormattedMessage id={item.messageId} />
      </summary>
      <ul className={css.secondaryNavigation}>
        {item.children.map(child => (
          <li key={child.id}>
            <DarkGloomyNavigationLink
              item={child}
              className={classNames(css.secondaryNavigationLink, {
                [css.currentSecondaryNavigationLink]: isNavigationItemCurrent(child, currentPage),
              })}
            />
          </li>
        ))}
      </ul>
    </details>
  );
};

const PrimaryNavigation = ({ currentPage }) => (
  <ul className={css.primaryNavigation}>
    {DARK_GLOOMY_NAVIGATION.map(item => {
      if (item.children) {
        return (
          <li key={item.id} className={css.primaryNavigationItem}>
            <NavigationGroup currentPage={currentPage} item={item} />
          </li>
        );
      }

      return (
        <li key={item.id} className={css.primaryNavigationItem}>
          <DarkGloomyNavigationLink item={item} className={css.primaryNavigationLink} />
        </li>
      );
    })}
  </ul>
);

const AccountNavigation = props => {
  const {
    currentPage,
    currentUser,
    inboxTab,
    isAuthenticated,
    notificationCount,
    onLogout,
    showCreateListingsLink,
  } = props;

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage
      ? css.currentAccountLink
      : null;
  };

  if (!isAuthenticated) {
    return (
      <div className={css.accountSection}>
        <p className={css.sectionLabel}>
          <FormattedMessage id="TopbarMobileMenu.accountSection" />
        </p>
        <div className={css.authenticationActions}>
          <NamedLink name="SignupPage" className={css.primaryAccountAction}>
            <FormattedMessage id="TopbarMobileMenu.signupLink" />
          </NamedLink>
          <NamedLink name="LoginPage" className={css.secondaryAccountAction}>
            <FormattedMessage id="TopbarMobileMenu.loginLink" />
          </NamedLink>
        </div>
      </div>
    );
  }

  const user = ensureCurrentUser(currentUser);
  const displayName = user.attributes.profile.firstName;
  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  return (
    <div className={css.accountSection}>
      <div className={css.accountIdentity}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
        <div>
          <p className={css.sectionLabel}>
            <FormattedMessage id="TopbarMobileMenu.accountSection" />
          </p>
          <p className={css.accountName}>{displayName}</p>
        </div>
      </div>
      <ul className={css.accountNavigation}>
        <li className={css.accountNavigationItem}>
          <NamedLink
            className={classNames(css.accountLink, currentPageClass(`InboxPage:${inboxTab}`))}
            name="InboxPage"
            params={{ tab: inboxTab }}
          >
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
          </NamedLink>
        </li>
        {showCreateListingsLink ? (
          <li className={css.accountNavigationItem}>
            <NamedLink
              className={classNames(css.accountLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
            </NamedLink>
          </li>
        ) : null}
        <li className={css.accountNavigationItem}>
          <NamedLink
            className={classNames(css.accountLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>
        </li>
        <li className={css.accountNavigationItem}>
          <NamedLink
            className={classNames(css.accountLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>
        </li>
      </ul>
      <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
        <FormattedMessage id="TopbarMobileMenu.logoutLink" />
      </InlineTextButton>
    </div>
  );
};

/**
 * Branded, accessible navigation drawer for the mobile marketplace layout.
 */
const TopbarMobileMenu = props => {
  const intl = useIntl();
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    notificationCount = 0,
    onLogout,
    showCreateListingsLink,
  } = props;

  const homeItem = DARK_GLOOMY_NAVIGATION.find(item => item.id === 'home');

  return (
    <nav
      className={css.root}
      aria-label={intl.formatMessage({ id: 'TopbarMobileMenu.navigationLabel' })}
    >
      <header className={css.menuHeader}>
        <Logo layout="mobile" marketplaceLockup />
      </header>

      <NamedLink name="SearchPage" className={css.searchLink}>
        <SearchIcon
          className={css.searchIcon}
          ariaLabel={intl.formatMessage({ id: 'Topbar.searchIcon' })}
        />
        <FormattedMessage id="TopbarMobileMenu.searchLink" />
      </NamedLink>

      <div className={css.menuBody}>
        <CurrencySelector />
        <PrimaryNavigation currentPage={currentPage} />
        <AccountNavigation
          currentPage={currentPage}
          currentUser={currentUser}
          inboxTab={inboxTab}
          isAuthenticated={isAuthenticated}
          notificationCount={notificationCount}
          onLogout={onLogout}
          showCreateListingsLink={showCreateListingsLink}
        />
      </div>

      <footer className={css.footer}>
        {showCreateListingsLink ? (
          <NamedLink className={css.createNewListingLink} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        ) : null}
        <DarkGloomyNavigationLink item={homeItem} className={css.returnToShopLink}>
          <FormattedMessage id="TopbarMobileMenu.returnToShop" />
          <span aria-hidden="true">&#8599;</span>
        </DarkGloomyNavigationLink>
      </footer>
    </nav>
  );
};

export default TopbarMobileMenu;
